import React, { useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { Heart, Menu, Search, ShoppingBag, User, X } from 'lucide-react';
import { useStore } from '../../context/StoreContext';
import { CATEGORIES } from '../../data/mock';

const cats = Object.entries(CATEGORIES);

function Dropdown({ label, to, gender }) {
  return (
    <div className="group relative">
      <NavLink to={to} className={({ isActive }) => `eyebrow inline-block py-6 transition-colors hover:text-gold ${isActive ? 'text-gold' : ''}`}>
        {label}
      </NavLink>
      <div className="invisible absolute left-1/2 top-full z-40 w-64 -translate-x-1/2 border border-stone-warm bg-ivory p-6 opacity-0 shadow-xl transition-all duration-300 group-hover:visible group-hover:opacity-100">
        {cats.map(([key, c]) => (
          <Link key={key} to={`/shop?gender=${gender}&category=${key}`} className="block py-2 text-sm text-espresso hover:text-gold">
            {c.label}
          </Link>
        ))}
      </div>
    </div>
  );
}

export default function Header() {
  const { wishlist, count, setCartOpen } = useStore();
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const [q, setQ] = useState('');
  const navigate = useNavigate();

  const topLinks = [
    { to: '/shop?category=skincare', label: 'Skincare' },
    { to: '/shop?category=grooming', label: 'Grooming' },
  ];
  const endLinks = [
    { to: '/shop?category=jewelry', label: 'Jewelry' },
    { to: '/shop?category=selfcare', label: 'Self-Care' },
    { to: '/lookbook', label: 'Lookbook' },
    { to: '/gift-finder', label: 'Gifts' },
    { to: '/bundles', label: 'Bundles' },
  ];

  return (
    <header className="sticky top-0 z-30 border-b border-stone-warm bg-ivory/95 backdrop-blur">
      <div className="bg-espresso py-2 text-center text-ivory eyebrow">Free delivery across South Africa on orders over R 800</div>
      <div className="relative mx-auto flex max-w-7xl items-center justify-between px-5 lg:px-8">
        <button className="lg:hidden py-5" onClick={() => setOpen(true)} aria-label="Open menu"><Menu className="size-5" /></button>

        <nav className="hidden items-center gap-7 lg:flex">
          <Dropdown label="Shop Men" to="/men" gender="men" />
          <Dropdown label="Shop Women" to="/women" gender="women" />
          <NavLink to="/unisex" className="eyebrow hover:text-gold">Unisex</NavLink>
          {topLinks.map((l) => (<Link key={l.label} to={l.to} className="eyebrow hover:text-gold">{l.label}</Link>))}
        </nav>

        <Link to="/" className="font-display text-2xl tracking-wide lg:absolute lg:left-1/2 lg:-translate-x-1/2">
          Aura <span className="text-gold">&amp;</span> Ore
        </Link>

        <div className="flex items-center gap-5">
          <nav className="hidden items-center gap-7 xl:flex">
            {endLinks.map((l) => (<Link key={l.label} to={l.to} className="eyebrow hover:text-gold">{l.label}</Link>))}
          </nav>
          <button onClick={() => setSearching((s) => !s)} aria-label="Search"><Search className="size-[18px]" /></button>
          <Link to="/account" aria-label="Account" className="hidden sm:block"><User className="size-[18px]" /></Link>
          <Link to="/wishlist" aria-label="Wishlist" className="relative">
            <Heart className="size-[18px]" />
            {wishlist.length > 0 && <span className="absolute -right-2 -top-2 grid size-4 place-items-center rounded-full bg-gold text-[9px] text-espresso">{wishlist.length}</span>}
          </Link>
          <button onClick={() => setCartOpen(true)} aria-label="Cart" className="relative">
            <ShoppingBag className="size-[18px]" />
            {count > 0 && <span className="absolute -right-2 -top-2 grid size-4 place-items-center rounded-full bg-gold text-[9px] text-espresso">{count}</span>}
          </button>
        </div>
      </div>

      {searching && (
        <form className="border-t border-stone-warm bg-ivory" onSubmit={(e) => { e.preventDefault(); setSearching(false); navigate(`/shop?q=${encodeURIComponent(q)}`); }}>
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search serums, chains, beard oil…" className="mx-auto block w-full max-w-3xl bg-transparent px-5 py-5 font-display text-2xl outline-none placeholder:text-muted-foreground" />
        </form>
      )}

      {open && (
        <div className="fixed inset-0 z-50 bg-ivory p-6 fade-in lg:hidden">
          <div className="flex justify-between">
            <span className="font-display text-2xl">Aura &amp; Ore</span>
            <button onClick={() => setOpen(false)} aria-label="Close menu"><X /></button>
          </div>
          <nav className="mt-10 flex flex-col gap-5 font-display text-3xl" onClick={() => setOpen(false)}>
            <Link to="/men">Shop Men</Link>
            <Link to="/women">Shop Women</Link>
            <Link to="/unisex">Unisex</Link>
            {[...topLinks, ...endLinks].map((l) => <Link key={l.label} to={l.to}>{l.label}</Link>)}
            <Link to="/about">About Us</Link>
            <Link to="/contact">Contact</Link>
          </nav>
        </div>
      )}
    </header>
  );
}
